// Commander abilities — battle-time hotkeys (Q/W/E/R) gated by Skill Tree nodes.
// All abilities consume a per-ability cooldown; activation requires the
// matching `MetaModifiers.abilities.<name>` flag from the Banner trunk.

import Phaser from "phaser";
import { Enemy } from "../entities/Enemy";
import { Tower } from "../entities/Tower";
import { computeMetaModifiers, MetaModifiers } from "./SkillTree";
import { sound } from "./Sound";

export type AbilityKey = "rally" | "meteor" | "heal" | "conscript";

interface AbilityDef {
  key: AbilityKey;
  hotkey: string;
  label: string;
  cooldownMs: number;
  /** Tooltip describing battle effect. */
  description: string;
}

const BASE_ABILITIES: readonly AbilityDef[] = [
  { key: "rally",     hotkey: "Q", label: "Rally",     cooldownMs: 30_000, description: "+30% fire rate to all towers for 8s." },
  { key: "meteor",    hotkey: "W", label: "Meteor",    cooldownMs: 60_000, description: "Drop a meteor at the cursor — 400 magic damage in 100px." },
  { key: "heal",      hotkey: "E", label: "Heal Keep", cooldownMs: 90_000, description: "Restore 5 castle HP." },
  { key: "conscript", hotkey: "R", label: "Conscript", cooldownMs: 45_000, description: "+200 gold immediately." }
];

export class AbilityController {
  private mods: MetaModifiers = computeMetaModifiers();
  private abilities: AbilityDef[];
  private cooldowns: Record<AbilityKey, number> = { rally: 0, meteor: 0, heal: 0, conscript: 0 };
  private rallyRemainingMs = 0;
  /** Listeners notified when cooldowns/buffs change (for HUD). */
  private listeners: ((s: AbilityState[]) => void)[] = [];

  constructor(
    private scene: Phaser.Scene,
    private getTowers: () => Tower[],
    private getEnemies: () => Enemy[],
    private healCastle: (amount: number) => void,
    private addGold: (amount: number) => void
  ) {
    // Apply meta cooldown reduction (lower is faster).
    const mult = this.mods.abilities.cooldownMult ?? 1;
    this.abilities = BASE_ABILITIES.map((ability) => ({
      ...ability,
      cooldownMs: Math.round(ability.cooldownMs * mult),
    }));
  }

  /** Returns whether this ability is unlocked through the Skill Tree. */
  isUnlocked(key: AbilityKey): boolean {
    return !!this.mods.abilities[key];
  }

  on(fn: (s: AbilityState[]) => void): () => void {
    this.listeners.push(fn);
    fn(this.snapshot());
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }
  private emit(): void { const s = this.snapshot(); for (const l of this.listeners) l(s); }
  snapshot(): AbilityState[] {
    return this.abilities.map(a => ({
      key: a.key,
      hotkey: a.hotkey,
      label: a.label,
      description: a.description,
      cooldownMs: a.cooldownMs,
      remainingMs: this.cooldowns[a.key],
      unlocked: this.isUnlocked(a.key),
      ready: this.isUnlocked(a.key) && this.cooldowns[a.key] <= 0
    }));
  }

  update(_time: number, deltaMs: number): void {
    let changed = false;
    for (const a of this.abilities) {
      if (this.cooldowns[a.key] > 0) {
        this.cooldowns[a.key] -= deltaMs;
        if (this.cooldowns[a.key] < 0) this.cooldowns[a.key] = 0;
        changed = true;
      }
    }
    // Rally buff fading is handled inline in `effectiveFireRate`-style accessors;
    // just emit on changes.
    if (this.rallyRemainingMs > 0) {
      this.rallyRemainingMs -= deltaMs;
      if (this.rallyRemainingMs <= 0) this.rallyRemainingMs = 0;
      changed = true;
    }
    if (changed) this.emit();
  }

  /** Returns the active rally fire-rate multiplier (1.0 = no rally). */
  rallyMult(_time?: number): number {
    return this.rallyRemainingMs > 0 ? 1.3 : 1.0;
  }

  /** Trigger an ability. Returns true if it fired. */
  cast(key: AbilityKey, pointer?: Phaser.Input.Pointer): boolean {
    if (!this.isUnlocked(key)) return false;
    if (this.cooldowns[key] > 0) return false;
    const def = this.abilities.find(a => a.key === key)!;
    switch (key) {
      case "rally":
        this.rallyRemainingMs = 8000;
        this.playRally();
        break;
      case "meteor": {
        if (!pointer) return false;
        const x = pointer.worldX;
        const y = pointer.worldY;
        const radius = 100;
        this.playMeteor(x, y, radius);
        break;
      }
      case "heal":
        this.healCastle(5);
        this.playHeal();
        break;
      case "conscript":
        this.addGold(200);
        this.playConscript();
        break;
    }
    sound.playAbility(key);
    this.cooldowns[key] = def.cooldownMs;
    this.emit();
    return true;
  }

  private flash(color: number): void {
    const cam = this.scene.cameras.main;
    cam.flash(180, (color >> 16) & 0xff, (color >> 8) & 0xff, color & 0xff, false);
  }

  private screenCenter(): { x: number; y: number } {
    const view = this.scene.cameras.main.worldView;
    return { x: view.centerX, y: view.centerY };
  }

  private playRally(): void {
    const { x, y } = this.screenCenter();
    const banner = this.scene.add.image(x, y, "ability-vfx-rally-banner")
      .setDisplaySize(170, 170).setScale(0.35).setAlpha(0).setDepth(1000)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.scene.tweens.add({
      targets: banner, scale: 1, alpha: 1, angle: 4, duration: 260, ease: "Back.Out",
      onComplete: () => this.scene.tweens.add({
        targets: banner, scale: 1.25, alpha: 0, delay: 520, duration: 520,
        ease: "Cubic.In", onComplete: () => banner.destroy()
      })
    });
    for (const tower of this.getTowers()) {
      const pulse = this.scene.add.circle(tower.x, tower.y, 8, 0x9ee56a, 0.18)
        .setStrokeStyle(2, 0xfff2a8, 0.85).setDepth(tower.depth + 2);
      this.scene.tweens.add({ targets: pulse, radius: 30, alpha: 0, duration: 620, onComplete: () => pulse.destroy() });
    }
    this.flash(0x88ff88);
  }

  private playMeteor(x: number, y: number, radius: number): void {
    const meteor = this.scene.add.image(x + 180, y - 220, "ability-meteor")
      .setDisplaySize(112, 112).setDepth(1100).setScale(0.72);
    this.scene.tweens.add({
      targets: meteor, x, y, scale: 1.05, duration: 430, ease: "Quad.In",
      onComplete: () => {
        meteor.destroy();
        for (const enemy of this.getEnemies()) {
          if (!enemy.alive) continue;
          const dx = enemy.x - x;
          const dy = enemy.y - y;
          if (dx * dx + dy * dy <= radius * radius) enemy.takeDamage(400, "magic");
        }
        const impact = this.scene.add.image(x, y, "ability-vfx-meteor-impact")
          .setDisplaySize(radius * 2.25, radius * 2.25).setScale(0.3).setDepth(1050)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.scene.tweens.add({
          targets: impact, scale: 1, alpha: 0, duration: 650, ease: "Cubic.Out",
          onComplete: () => impact.destroy()
        });
      }
    });
  }

  private playHeal(): void {
    const { x, y } = this.screenCenter();
    const beam = this.scene.add.image(x, y - 40, "ability-vfx-heal-beam")
      .setDisplaySize(160, 300).setAlpha(0).setDepth(980).setBlendMode(Phaser.BlendModes.ADD);
    const shield = this.scene.add.image(x, y + 28, "ability-vfx-heal-shield")
      .setDisplaySize(110, 110).setScale(0.35).setAlpha(0).setDepth(1000);
    this.scene.tweens.add({ targets: beam, alpha: 0.9, duration: 180, yoyo: true, hold: 420,
      onComplete: () => beam.destroy() });
    this.scene.tweens.add({ targets: shield, scale: 1, alpha: 1, duration: 260, ease: "Back.Out",
      onComplete: () => this.scene.tweens.add({ targets: shield, alpha: 0, y: shield.y - 20, delay: 420,
        duration: 360, onComplete: () => shield.destroy() }) });
    this.flash(0xffe070);
  }

  private playConscript(): void {
    const { x, y } = this.screenCenter();
    const circle = this.scene.add.image(x, y + 30, "ability-vfx-conscript-circle")
      .setDisplaySize(150, 150).setScale(0.25).setAlpha(0).setDepth(970)
      .setBlendMode(Phaser.BlendModes.ADD);
    const coins = this.scene.add.image(x, y, "ability-vfx-conscript-coins")
      .setDisplaySize(105, 105).setScale(0.4).setAlpha(0).setDepth(990);
    this.scene.tweens.add({ targets: circle, scale: 1, alpha: 0.85, angle: 20, duration: 360, ease: "Cubic.Out",
      onComplete: () => this.scene.tweens.add({ targets: circle, alpha: 0, delay: 420, duration: 380,
        onComplete: () => circle.destroy() }) });
    this.scene.tweens.add({ targets: coins, scale: 1, alpha: 1, y: y - 18, duration: 300, ease: "Back.Out",
      onComplete: () => this.scene.tweens.add({ targets: coins, alpha: 0, y: y - 42, delay: 420, duration: 360,
        onComplete: () => coins.destroy() }) });
    this.flash(0xffe070);
  }
}

export interface AbilityState {
  key: AbilityKey;
  hotkey: string;
  label: string;
  description: string;
  cooldownMs: number;
  remainingMs: number;
  unlocked: boolean;
  ready: boolean;
}
